import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

type MethodResponse = { draft: string };

type MethodForm = {
  company: string;
  project: string;
  site: string;
  supervisor: string;
  date: string;
  scope: string;
  sequence: string;
  plantTools: string;
  materials: string;
  permits: string;
  interfaces: string;
  emergency: string;
  environmental: string;
  notes: string;
  hotWorks: boolean;
  workingAtHeight: boolean;
  liftingOps: boolean;
  excavations: boolean;
  electricalIsolation: boolean;
  test: boolean;
};

const LS_KEY = "lumia:method:v1";

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export default function Method() {
  const [form, setForm] = useState<MethodForm>(() => {
    const saved = safeJsonParse<Partial<MethodForm>>(localStorage.getItem(LS_KEY));
    const base: MethodForm = {
      company: "",
      project: "",
      site: "",
      supervisor: "",
      date: todayISO(),
      scope: "",
      sequence: "",
      plantTools: "",
      materials: "",
      permits: "",
      interfaces: "",
      emergency: "",
      environmental: "",
      notes: "",
      hotWorks: false,
      workingAtHeight: false,
      liftingOps: false,
      excavations: false,
      electricalIsolation: false,
      test: true,
    };
    return { ...base, ...(saved ?? {}) } as MethodForm;
  });

  const [draft, setDraft] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState<string>("");
  const [lastPayload, setLastPayload] = useState<any>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        ...form,
      } satisfies Partial<MethodForm>)
    );
  }, [form]);

  const canSubmit = useMemo(() => {
    return (
      form.company.trim() &&
      form.project.trim() &&
      form.site.trim() &&
      form.supervisor.trim() &&
      form.date.trim() &&
      (form.scope.trim() || form.notes.trim()) &&
      !isLoading
    );
  }, [form, isLoading]);

  function update<K extends keyof MethodForm>(key: K, value: MethodForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(payloadOverride?: any) {
    setErrMsg("");
    setDraft("");

    const payload =
      payloadOverride ??
      ({
        company: form.company,
        project: form.project,
        site: form.site,
        supervisor: form.supervisor,
        date: form.date,
        scope: form.scope,
        sequence: form.sequence,
        plantTools: form.plantTools,
        materials: form.materials,
        permits: form.permits,
        interfaces: form.interfaces,
        emergency: form.emergency,
        environmental: form.environmental,
        notes: form.notes,
        hotWorks: form.hotWorks,
        workingAtHeight: form.workingAtHeight,
        liftingOps: form.liftingOps,
        excavations: form.excavations,
        electricalIsolation: form.electricalIsolation,
        test: form.test,
      } as const);

    setLastPayload(payload);

    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setIsLoading(true);
    try {
      const res = await fetch("/api/method", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        signal: ac.signal,
      });

      const text = await res.text();
      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        // ignore non-JSON error payloads
      }

      if (!res.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          `Request failed (${res.status})`;
        throw new Error(msg);
      }

      const parsed = data as MethodResponse;
      if (!parsed?.draft) throw new Error("No draft returned from API.");
      setDraft(parsed.draft);
    } catch (e: any) {
      if (e?.name === "AbortError") setErrMsg("Cancelled.");
      else setErrMsg(e?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  }

  function cancel() {
    if (abortRef.current) abortRef.current.abort();
  }

  async function copyDraft() {
    setErrMsg("");
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = draft;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        setErrMsg("Couldn’t copy to clipboard (browser restriction).");
      }
    }
  }

  async function downloadPdf() {
    setErrMsg("");
    if (!draft) return;
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const lines = doc.splitTextToSize(draft, 180);
      doc.text(lines, 15, 20);
      doc.save(`method-statement-${(form.project || "project").replace(/\s+/g, "-")}.pdf`);
    } catch {
      setErrMsg("PDF generation failed. Check jspdf is installed and bundled.");
    }
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>/method</h1>
      <p style={{ marginTop: 0, opacity: 0.85 }}>
        Construction Method Statement (UK) draft generator.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
          marginTop: 16,
          marginBottom: 12,
        }}
      >
        <label>
          Company
          <input
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            style={{ width: "100%" }}
            placeholder="e.g. Lumia Construction Ltd"
          />
        </label>

        <label>
          Project
          <input
            value={form.project}
            onChange={(e) => update("project", e.target.value)}
            style={{ width: "100%" }}
            placeholder="e.g. Kitchen refurb"
          />
        </label>

        <label>
          Site
          <input
            value={form.site}
            onChange={(e) => update("site", e.target.value)}
            style={{ width: "100%" }}
            placeholder="e.g. 10 High Street, London"
          />
        </label>

        <label>
          Supervisor / Person in charge
          <input
            value={form.supervisor}
            onChange={(e) => update("supervisor", e.target.value)}
            style={{ width: "100%" }}
            placeholder="e.g. J. Smith"
          />
        </label>

        <label>
          Date
          <input
            type="date"
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
        <label>
          Scope of works (plain English)
          <textarea
            value={form.scope}
            onChange={(e) => update("scope", e.target.value)}
            style={{ width: "100%", minHeight: 110 }}
            placeholder="What are we doing? What’s included/excluded?"
          />
        </label>

        <label>
          Sequence of works (steps)
          <textarea
            value={form.sequence}
            onChange={(e) => update("sequence", e.target.value)}
            style={{ width: "100%", minHeight: 110 }}
            placeholder="Step-by-step method…"
          />
        </label>

        <label>
          Plant / tools
          <textarea
            value={form.plantTools}
            onChange={(e) => update("plantTools", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="e.g. SDS drill, circular saw, dust extractor…"
          />
        </label>

        <label>
          Materials / substances
          <textarea
            value={form.materials}
            onChange={(e) => update("materials", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="e.g. timber, adhesives, silica dust, solvents…"
          />
        </label>

        <label>
          Permits / authorisations
          <textarea
            value={form.permits}
            onChange={(e) => update("permits", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="e.g. hot works permit, asbestos info, isolation permit…"
          />
        </label>

        <label>
          Interfaces / neighbours / public
          <textarea
            value={form.interfaces}
            onChange={(e) => update("interfaces", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="Other trades, residents, deliveries, pedestrians…"
          />
        </label>

        <label>
          Emergency arrangements
          <textarea
            value={form.emergency}
            onChange={(e) => update("emergency", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="First aid, fire, evacuation route, nearest A&E…"
          />
        </label>

        <label>
          Environmental controls
          <textarea
            value={form.environmental}
            onChange={(e) => update("environmental", e.target.value)}
            style={{ width: "100%", minHeight: 90 }}
            placeholder="Dust/noise, waste, spill control, water protection…"
          />
        </label>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.hotWorks} onChange={(e) => update("hotWorks", e.target.checked)} />
            Hot works
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.workingAtHeight}
              onChange={(e) => update("workingAtHeight", e.target.checked)}
            />
            Working at height
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.liftingOps} onChange={(e) => update("liftingOps", e.target.checked)} />
            Lifting operations
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.excavations}
              onChange={(e) => update("excavations", e.target.checked)}
            />
            Excavations / groundworks
          </label>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.electricalIsolation}
              onChange={(e) => update("electricalIsolation", e.target.checked)}
            />
            Electrical isolation / live services
          </label>
        </div>

        <label>
          Extra notes (hazards, constraints, anything weird)
          <textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            style={{ width: "100%", minHeight: 120 }}
            placeholder="Any special hazards, constraints, access issues, client rules, etc."
          />
        </label>

        <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={form.test} onChange={(e) => update("test", e.target.checked)} />
          Test mode (limited, cheaper)
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
        <button onClick={() => submit()} disabled={!canSubmit} style={{ padding: "10px 14px" }}>
          {isLoading ? "Generating…" : "Generate method statement"}
        </button>

        <button onClick={cancel} disabled={!isLoading} style={{ padding: "10px 14px" }}>
          Cancel
        </button>

        <button onClick={copyDraft} disabled={!draft} style={{ padding: "10px 14px" }}>
          Copy
        </button>

        <button onClick={downloadPdf} disabled={!draft} style={{ padding: "10px 14px" }}>
          Download PDF
        </button>

        <button onClick={() => lastPayload && submit(lastPayload)} disabled={!lastPayload || isLoading} style={{ padding: "10px 14px" }}>
          Retry last
        </button>
      </div>

      {errMsg ? (
        <div
          role="alert"
          style={{
            border: "1px solid rgba(255,0,0,0.35)",
            background: "rgba(255,0,0,0.06)",
            padding: 12,
            borderRadius: 8,
            marginTop: 12,
            whiteSpace: "pre-wrap",
          }}
        >
          {errMsg}
        </div>
      ) : null}

      {draft ? (
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 10,
            padding: 16,
            marginTop: 12,
          }}
        >
          <ReactMarkdown>{draft}</ReactMarkdown>
        </div>
      ) : null}
    </div>
  );
}
